/**
 * Security utilities for spam prevention and threat monitoring.
 * - Honeypot field validation
 * - Failed attempt logging
 * - Duplicate order prevention
 */

const SECURITY_LOG_PREFIX = "weborder.securitylog.";
const MAX_LOG_ENTRIES = 100;
const LOG_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// ─── Honeypot Validation ───

/**
 * Create a honeypot field value for forms.
 * Returns an empty string — the field should be hidden from real users.
 * If a bot fills it, the value will be non-empty.
 */
export function createHoneypotInitialState() {
  return { website: "", name_hp: "" }; // honeypot fields
}

/**
 * Validate honeypot fields. Returns true if the form appears to be from a bot.
 * @param {object} honeypot - The honeypot field values
 * @returns {boolean} true if bot detected (honeypot filled)
 */
export function isHoneypotTriggered(honeypot) {
  if (!honeypot) return false;
  return Boolean(honeypot.website || honeypot.name_hp);
}

// ─── Failed Attempt Logging ───

/**
 * Log a failed security event.
 * @param {string} action - e.g. "login_failed", "register_failed", "checkout_failed"
 * @param {object} details - Additional context (email, error message, etc.)
 */
export function logSecurityEvent(action, details = {}) {
  const key = SECURITY_LOG_PREFIX + "events";
  const now = Date.now();

  let events = [];
  try {
    events = JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    events = [];
  }

  events.push({
    action,
    timestamp: now,
    isoDate: new Date().toISOString(),
    details: {
      ...details,
      // Don't store sensitive data like passwords
      email: details.email ? details.email.substring(0, 3) + "***" : undefined,
    },
  });

  // Remove expired entries
  events = events.filter((e) => now - e.timestamp < LOG_EXPIRY_MS);

  // Cap entries
  if (events.length > MAX_LOG_ENTRIES) {
    events = events.slice(-MAX_LOG_ENTRIES);
  }

  try {
    localStorage.setItem(key, JSON.stringify(events));
  } catch {
    // localStorage full — clear old entries
    localStorage.removeItem(key);
  }
}

/**
 * Get recent security events for monitoring.
 * @param {number} limit - Max events to return
 * @returns {Array}
 */
export function getSecurityEvents(limit = 50) {
  const key = SECURITY_LOG_PREFIX + "events";
  const now = Date.now();

  let events = [];
  try {
    events = JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    events = [];
  }

  return events
    .filter((e) => now - e.timestamp < LOG_EXPIRY_MS)
    .slice(-limit);
}

/**
 * Count failed attempts for a specific action in the last N minutes.
 * @param {string} action
 * @param {number} minutes
 * @returns {number}
 */
export function countRecentFailures(action, minutes = 30) {
  const events = getSecurityEvents();
  const cutoff = Date.now() - minutes * 60 * 1000;
  return events.filter((e) => e.action === action && e.timestamp > cutoff).length;
}

/**
 * Clear all security logs.
 */
export function clearSecurityLogs() {
  const keys = Object.keys(localStorage).filter((k) => k.startsWith(SECURITY_LOG_PREFIX));
  keys.forEach((k) => localStorage.removeItem(k));
}

// ─── Duplicate Order Prevention ───

const PENDING_ORDERS_KEY = "weborder.pendingOrders";

/**
 * Check if user already has a pending order for a specific app.
 * @param {string} userId
 * @param {string} appId
 * @returns {boolean}
 */
export function hasPendingOrder(userId, appId) {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_ORDERS_KEY) || "[]");
    return pending.some(
      (o) => o.userId === userId && o.appId === appId && Date.now() - o.timestamp < 30 * 60 * 1000
    );
  } catch {
    return false;
  }
}

/**
 * Record a pending order to prevent duplicates.
 * @param {string} userId
 * @param {string} appId
 * @param {string} orderId
 */
export function recordPendingOrder(userId, appId, orderId) {
  try {
    let pending = JSON.parse(localStorage.getItem(PENDING_ORDERS_KEY) || "[]");
    pending.push({ userId, appId, orderId, timestamp: Date.now() });
    // Clean old entries (>30 min)
    pending = pending.filter((o) => Date.now() - o.timestamp < 30 * 60 * 1000);
    localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(pending));
  } catch {
    // ignore
  }
}

/**
 * Remove a pending order record (after completion/failure).
 * @param {string} orderId
 */
export function removePendingOrder(orderId) {
  try {
    let pending = JSON.parse(localStorage.getItem(PENDING_ORDERS_KEY) || "[]");
    pending = pending.filter((o) => o.orderId !== orderId);
    localStorage.setItem(PENDING_ORDERS_KEY, JSON.stringify(pending));
  } catch {
    // ignore
  }
}